import { ButtonHTMLAttributes, forwardRef } from "react";

/**
 * Button — Archival Studio primitive.
 *
 * Variants per design package (project/preview/components-buttons.html):
 *   primary  — ink fill, paper-raised text. The default action.
 *   walnut   — walnut fill, paper-raised text. Editorial primary
 *              (e.g. "Run proof gate", "Verify").
 *   secondary — transparent w/ rule-strong outline. "Open in reader".
 *   ghost    — transparent, no border. "Discard take".
 *
 * Size: sm | md | lg.
 * Sentence case enforced by convention; brand voice rule.
 */

type Variant = "primary" | "walnut" | "secondary" | "ghost";
type Size = "sm" | "md" | "lg";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
}

const VARIANT: Record<Variant, string> = {
  primary:
    "bg-walnut text-paper-raised border-0 hover:bg-walnut-deep active:shadow-as-press",
  walnut:
    "bg-walnut text-paper-raised border-0 hover:bg-walnut-deep active:shadow-as-press",
  secondary:
    "bg-paper-raised/70 text-ink-1 border border-rule-faint hover:border-rule hover:bg-paper-raised",
  ghost:
    "bg-transparent text-ink-2 border-0 hover:text-walnut-deep",
};

const SIZE: Record<Size, string> = {
  sm: "text-t-13 px-[12px] py-[6px] font-semibold",
  md: "text-t-14 px-[15px] py-[8px] font-semibold",
  lg: "text-t-14 px-[18px] py-[10px] font-semibold",
};

const BASE =
  "inline-flex max-w-full items-center justify-center font-ui rounded-as-pill " +
  "min-h-11 " +
  "shadow-[0_1px_1px_rgba(25,24,20,0.04)] transition-[background-color,color,border-color,box-shadow,transform] duration-as-1 ease-as-out " +
  "active:translate-y-px " +
  "disabled:opacity-50 disabled:cursor-not-allowed select-none";

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "primary", size = "md", className = "", ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      className={`${BASE} ${VARIANT[variant]} ${SIZE[size]} ${className}`}
      {...rest}
    />
  );
});
